import {
  UserIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  AdjustmentsHorizontalIcon,
  ScaleIcon,
  ChartBarIcon,
  DocumentChartBarIcon,
  TvIcon,
  PhotoIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/outline";
import {
  NavigationItemConfig,
  NestedGroupConfig,
} from "../utils/navigationUtils";

// Direct navigation items (always shown in top bar)
export const directNavigationItems: NavigationItemConfig[] = [
  {
    id: "dashboard",
    name: "Overview",
    route: "/dashboard",
    module: "dashboard",
  },
  {
    id: "screens",
    name: "Screens",
    route: "/screens",
    module: "screens",
  },
  {
    id: "playlist",
    name: "Playlists",
    route: "/playlist",
    module: "playlist",
  },
  {
    id: "clientplaylist",
    name: "Client Playlists",
    route: "/clientplaylist",
    module: "clientplaylist",
  },
  {
    id: "creatives",
    name: "Creatives",
    route: "/creatives",
    module: "creatives",
  },
  {
    id: "scheduler",
    name: "Scheduler",
    route: "/scheduler",
    module: "scheduler",
  },
  {
    id: "schedules",
    name: "Schedules",
    route: "/schedules",
    module: "scheduler",
  },
];

// Items that should be direct for superadmin but nested for regular users
export const superAdminDirectItems: NavigationItemConfig[] = [
  {
    id: "tenants",
    name: "Tenants",
    route: "/tenants",
    module: "tenants",
    icon: BuildingStorefrontIcon,
    showForSuperAdmin: true,
    showInNested: true,
  },
  {
    id: "roles",
    name: "Roles",
    route: "/roles",
    module: "roles",
    icon: ShieldCheckIcon,
    showForSuperAdmin: true,
    showInNested: true,
  },
  {
    id: "permissions",
    name: "Permissions",
    route: "/permissions",
    module: "permissions",
    icon: ShieldCheckIcon,
    showForSuperAdmin: true,
    showInNested: true,
  },
  {
    id: "pisoftwareupdates",
    name: "Pi Software Updates",
    route: "/pisoftwareupdates",
    module: "pi_ota",
    icon: CloudArrowUpIcon,
    showForSuperAdmin: true,
    showInNested: false,
  },
];

// Nested navigation groups
export const nestedGroups: NestedGroupConfig[] = [
  {
    id: "admin",
    name: "Admin",
    route: "/admin",
    items: [
      {
        id: "users",
        name: "Users",
        route: "/admin/users",
        module: "users",
        icon: UserIcon,
        showForSuperAdmin: false,
        showInNested: true,
      },
      {
        id: "roles",
        name: "Roles",
        route: "/admin/roles",
        module: "roles",
        icon: ShieldCheckIcon,
        showForSuperAdmin: false,
        showInNested: true,
      },
      {
        id: "clients",
        name: "Clients",
        route: "/admin/clients",
        module: "clients",
        icon: BuildingOfficeIcon,
        showForSuperAdmin: false,
        showInNested: true,
      },
      {
        id: "brands",
        name: "Brands",
        route: "/admin/brands",
        module: "brands",
        icon: BuildingStorefrontIcon,
        showForSuperAdmin: false,
        showInNested: true,
      },
    ],
    visibilityCheck: (userModules, isSuperAdmin) => {
      // For superadmin, only show if they have users or clients (roles/tenants/permissions are direct)
      if (isSuperAdmin) {
        return (
          userModules.has("users") ||
          userModules.has("clients") ||
          userModules.has("brands")
        );
      }
      // For regular users, show if they have any admin item
      return (
        userModules.has("users") ||
        userModules.has("roles") ||
        userModules.has("clients") ||
        userModules.has("brands")
      );
    },
  },
  {
    id: "controlpanel",
    name: "Control Panel",
    route: "/controlpanel",
    items: [
      {
        id: "screenmanagement",
        name: "Screen Management",
        route: "/controlpanel/screenmanagement",
        module: "screens",
        icon: TvIcon,
        showInNested: true,
      },
      {
        id: "accountmanagement",
        name: "Account Management",
        route: "/controlpanel/accountmanagement",
        module: "accountmanagement",
        icon: Cog6ToothIcon,
        showInNested: true,
      },
      {
        id: "templates",
        name: "Templates",
        route: "/controlpanel/templates",
        module: "templates",
        icon: DocumentTextIcon,
        showInNested: true,
      },
      {
        id: "tenantconfiguration",
        name: "Tenant Configuration",
        route: "/controlpanel/tenantconfiguration",
        module: "tenantconfiguration",
        icon: AdjustmentsHorizontalIcon,
        showInNested: true,
      },
      {
        id: "tenantassets",
        name: "Tenant Assets",
        route: "/controlpanel/tenantassets",
        module: "tenantconfiguration",
        icon: PhotoIcon,
        showInNested: true,
      },
      {
        id: "termsandconditions",
        name: "Terms and Conditions",
        route: "/controlpanel/termsandconditions",
        module: "tenantconfiguration",
        icon: ScaleIcon, // Legal/terms icon - alternative: ClipboardDocumentCheckIcon
        showInNested: true,
      },
    ],
    visibilityCheck: (userModules) => {
      return (
        userModules.has("screens") ||
        userModules.has("accountmanagement") ||
        userModules.has("templates") ||
        userModules.has("tenantconfiguration")
      );
    },
  },
  {
    id: "reports",
    name: "Reports",
    route: "/reports",
    items: [
      {
        id: "onoffreport",
        name: "On/Off Report",
        route: "/reports/onoff",
        module: "reports",
        icon: ChartBarIcon,
        showInNested: true,
      },
      {
        id: "creativereport",
        name: "Creative Report",
        route: "/reports/creative",
        module: "reports",
        icon: DocumentChartBarIcon,
        showInNested: true,
      },
      {
        id: "activescreensreport",
        name: "Active Screens Report",
        route: "/reports/activescreens",
        module: "reports",
        icon: TvIcon,
        showInNested: true,
      },
    ],
    visibilityCheck: (userModules) => {
      return userModules.has("reports");
    },
  },
];
